/**
 * Frontend Access Control Constants
 *
 * Mirrors backend access control system for consistent UX
 * Now integrates with premium configuration system for flexible feature control
 */

import { StockCategoryFilter } from '../types/stockCategories';
import { getPremiumConfig, isCategoryPremium as checkCategoryPremium } from '../config/premiumConfig';
import { ALL_CATEGORIES } from '../config/categoryConfig';

// Re-export for backward compatibility
export { ALL_CATEGORIES };

/**
 * Get categories that require premium access (dynamic based on config)
 */
export function getPremiumCategories(): readonly StockCategoryFilter[] {
  const config = getPremiumConfig();

  // In FREE mode, no categories are premium
  if (config.mode === 'FREE') {
    return [] as const;
  }

  return config.premiumCategories as readonly StockCategoryFilter[];
}

/**
 * Get categories accessible by free users (dynamic based on config)
 */
export function getFreeCategories(): readonly StockCategoryFilter[] {
  const premiumCats = getPremiumCategories();
  return ALL_CATEGORIES.filter(cat => !premiumCats.includes(cat)) as readonly StockCategoryFilter[];
}

/**
 * @deprecated Use getPremiumCategories() for dynamic configuration
 * Legacy constant for backward compatibility
 */
export const PREMIUM_CATEGORIES: readonly StockCategoryFilter[] = [
  'pharma',
  'financial',
  'consumer',
  'industrial',
  'energy',
  'foreign'
] as const;

/**
 * @deprecated Use getFreeCategories() for dynamic configuration
 * Legacy constant for backward compatibility
 */
export const FREE_CATEGORIES: readonly StockCategoryFilter[] = ['popular', 'tech', 'wallstreetbets'] as const;

/**
 * Maximum private leaderboards for free users
 */
export const FREE_LEADERBOARD_LIMIT = 1;

/**
 * User tier types
 */
export type UserTier = 'anonymous' | 'free' | 'premium';

/**
 * Get feature flags by user tier (dynamic based on premium config)
 */
export function getFeatureFlags(userTier: UserTier) {
  const config = getPremiumConfig();
  const freeCategories = getFreeCategories();
  const allCategories = ALL_CATEGORIES;

  // In FREE mode, everyone gets full access
  if (config.mode === 'FREE') {
    return {
      anonymous: {
        canPlayCategories: allCategories,
        canSaveProgress: false,
        canViewLeaderboard: true,
        canViewOwnRank: false,
        canVoteSentiment: false,
        canJoinPrivateLeaderboards: false,
        canViewSentimentHistory: false,
        maxPrivateLeaderboards: 0
      },
      free: {
        canPlayCategories: allCategories,
        canSaveProgress: true,
        canViewLeaderboard: true,
        canViewOwnRank: true,
        canVoteSentiment: true,
        canJoinPrivateLeaderboards: true,
        canViewSentimentHistory: true, // Unlocked in FREE mode
        maxPrivateLeaderboards: Infinity
      },
      premium: {
        canPlayCategories: allCategories,
        canSaveProgress: true,
        canViewLeaderboard: true,
        canViewOwnRank: true,
        canVoteSentiment: true,
        canJoinPrivateLeaderboards: true,
        canViewSentimentHistory: true,
        maxPrivateLeaderboards: Infinity
      }
    };
  }

  // HIDE and STANDARD modes: normal premium gating
  return {
    anonymous: {
      canPlayCategories: freeCategories,
      canSaveProgress: false,
      canViewLeaderboard: true, // Can see top 5
      canViewOwnRank: false,
      canVoteSentiment: false,
      canJoinPrivateLeaderboards: false,
      canViewSentimentHistory: false,
      maxPrivateLeaderboards: 0
    },
    free: {
      canPlayCategories: freeCategories,
      canSaveProgress: true,
      canViewLeaderboard: true,
      canViewOwnRank: true,
      canVoteSentiment: true,
      canJoinPrivateLeaderboards: true,
      canViewSentimentHistory: false,
      maxPrivateLeaderboards: FREE_LEADERBOARD_LIMIT
    },
    premium: {
      canPlayCategories: allCategories,
      canSaveProgress: true,
      canViewLeaderboard: true,
      canViewOwnRank: true,
      canVoteSentiment: true,
      canJoinPrivateLeaderboards: true,
      canViewSentimentHistory: true,
      maxPrivateLeaderboards: Infinity
    }
  };
}

/**
 * @deprecated Use getFeatureFlags() for dynamic configuration
 * Legacy constant for backward compatibility
 */
export const FEATURE_FLAGS = {
  anonymous: {
    canPlayCategories: FREE_CATEGORIES,
    canSaveProgress: false,
    canViewLeaderboard: true, // Can see top 5
    canViewOwnRank: false,
    canVoteSentiment: false,
    canJoinPrivateLeaderboards: false,
    canViewSentimentHistory: false,
    maxPrivateLeaderboards: 0
  },
  free: {
    canPlayCategories: FREE_CATEGORIES,
    canSaveProgress: true,
    canViewLeaderboard: true,
    canViewOwnRank: true,
    canVoteSentiment: true,
    canJoinPrivateLeaderboards: true,
    canViewSentimentHistory: false,
    maxPrivateLeaderboards: FREE_LEADERBOARD_LIMIT
  },
  premium: {
    canPlayCategories: ALL_CATEGORIES,
    canSaveProgress: true,
    canViewLeaderboard: true,
    canViewOwnRank: true,
    canVoteSentiment: true,
    canJoinPrivateLeaderboards: true,
    canViewSentimentHistory: true,
    maxPrivateLeaderboards: Infinity
  }
} as const;

/**
 * Access check result
 */
export interface AccessCheckResult {
  allowed: boolean;
  reason?: string;
  requiresAuth?: boolean;
  requiresPremium?: boolean;
  upgradeMessage?: string;
}

/**
 * Type guard to check if category requires premium
 */
export function isPremiumCategory(category: StockCategoryFilter): boolean {
  return checkCategoryPremium(category as any);
}

/**
 * Type guard to check if category is free
 */
export function isFreeCategory(category: StockCategoryFilter): boolean {
  return !isPremiumCategory(category);
}

/**
 * Get user tier based on authentication status
 */
export function getUserTier(isAuthenticated: boolean, isPremium: boolean): UserTier {
  if (!isAuthenticated) return 'anonymous';
  if (isPremium) return 'premium';
  return 'free';
}

/**
 * Check if user can access a specific category
 */
export function canAccessCategory(
  category: StockCategoryFilter,
  userTier: UserTier
): AccessCheckResult {
  const featureFlags = getFeatureFlags(userTier);
  const features = featureFlags[userTier];

  if (features.canPlayCategories.includes(category)) {
    return { allowed: true };
  }

  if (userTier === 'anonymous') {
    // For premium categories, show the upgrade prompt directly instead of
    // asking to sign up first — avoids a misleading two-step funnel.
    if (checkCategoryPremium(category as any)) {
      return {
        allowed: false,
        requiresPremium: true,
        reason: `Premium subscription required for ${category.toUpperCase()} category`,
        upgradeMessage: 'Upgrade to Premium to unlock all categories'
      };
    }
    return {
      allowed: false,
      requiresAuth: true,
      reason: `Sign up to unlock the ${category.toUpperCase()} category`,
      upgradeMessage: 'Create a free account to save your progress and unlock more features'
    };
  }

  if (userTier === 'free') {
    return {
      allowed: false,
      requiresPremium: true,
      reason: `Premium subscription required for ${category.toUpperCase()} category`,
      upgradeMessage: 'Upgrade to Premium to unlock all categories'
    };
  }

  return { allowed: true };
}

/**
 * Check if user can vote on sentiment
 */
export function canVoteSentiment(userTier: UserTier): AccessCheckResult {
  const featureFlags = getFeatureFlags(userTier);
  const features = featureFlags[userTier];

  if (features.canVoteSentiment) {
    return { allowed: true };
  }

  return {
    allowed: false,
    requiresAuth: true,
    reason: 'Sign in to vote on stock sentiment',
    upgradeMessage: 'Create a free account to vote on stock difficulty'
  };
}

/**
 * Check if user can view sentiment history
 */
export function canViewSentimentHistory(userTier: UserTier): AccessCheckResult {
  const featureFlags = getFeatureFlags(userTier);
  const features = featureFlags[userTier];

  if (features.canViewSentimentHistory) {
    return { allowed: true };
  }

  if (userTier === 'anonymous') {
    return {
      allowed: false,
      requiresAuth: true,
      reason: 'Sign in to view sentiment history',
      upgradeMessage: 'Create an account to access sentiment history'
    };
  }

  return {
    allowed: false,
    requiresPremium: true,
    reason: 'Premium subscription required to view sentiment history',
    upgradeMessage: 'Upgrade to Premium to view historical sentiment data'
  };
}

/**
 * Check if user can create/join private leaderboards
 */
export function canJoinPrivateLeaderboard(
  userTier: UserTier,
  currentLeaderboardCount: number
): AccessCheckResult {
  const featureFlags = getFeatureFlags(userTier);
  const features = featureFlags[userTier];

  if (!features.canJoinPrivateLeaderboards) {
    return {
      allowed: false,
      requiresAuth: true,
      reason: 'Sign in to join private leaderboards',
      upgradeMessage: 'Create a free account to join or create 1 private leaderboard'
    };
  }

  if (currentLeaderboardCount >= features.maxPrivateLeaderboards) {
    return {
      allowed: false,
      requiresPremium: true,
      reason: `You have reached the limit of ${features.maxPrivateLeaderboards} private leaderboard(s)`,
      upgradeMessage: 'Upgrade to Premium for unlimited private leaderboards'
    };
  }

  return { allowed: true };
}

/**
 * Get accessible categories for a user tier
 */
export function getAccessibleCategories(userTier: UserTier): readonly StockCategoryFilter[] {
  const featureFlags = getFeatureFlags(userTier);
  return featureFlags[userTier].canPlayCategories;
}

/**
 * Get restricted categories for a user tier
 */
export function getRestrictedCategories(userTier: UserTier): readonly StockCategoryFilter[] {
  const featureFlags = getFeatureFlags(userTier);
  const accessible = featureFlags[userTier].canPlayCategories;
  return ALL_CATEGORIES.filter(cat => !accessible.includes(cat));
}

/**
 * Format upgrade message for UI display
 */
export function formatUpgradeMessage(accessCheck: AccessCheckResult): string {
  if (accessCheck.requiresAuth) {
    return accessCheck.upgradeMessage || 'Sign up to unlock this feature';
  }
  if (accessCheck.requiresPremium) {
    return accessCheck.upgradeMessage || 'Upgrade to Premium to unlock this feature';
  }
  return '';
}

/**
 * Get feature access for a user tier
 */
export function getFeatureAccess(userTier: UserTier) {
  const featureFlags = getFeatureFlags(userTier);
  return {
    ...featureFlags[userTier],
    tier: userTier
  };
}
